import React from "react";
import { Box, Card, CardContent, CardHeader, ToggleButton, ToggleButtonGroup } from "@mui/material";
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import _ from 'lodash';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import ChartTooltip from "../../../charts/ChartTooltip";
import ChartEmpty from "../../../charts/ChartEmpty";
import { DUAL_AXIS_COLORS } from "../../../charts/colors";
import { formatNumber, ndrReasons, numberPrecision, transformChartDataToTable } from "../../../../helpers/UtilityHelper";
import MaterialTable from "../../../../common/MaterialTable";

export default function ReasonAnalysis({ data }) {
    const [view, setView] = React.useState("chart")

    const reasonKeys = Object.keys(ndrReasons)
    const { rows, allColumns } = transformChartDataToTable(data, [{ key: 'totalRtoCases', 'label': 'Total RTO', order: 1 }, ...reasonKeys.map(r => ({ key: `${r}_value`, label: ndrReasons[r] }))])

    const tableColumns = [
        {
            label: 'Name',
            value: 'name',
            align: 'left'
        },
        ...(allColumns.map((z) => ({
            numeric: true,
            align: 'right',
            value: (row) => {
                return formatNumber(row[z])
            },
            label: z
        })))
    ]

    const grandTotal = _.sumBy(data || [], 'totalRtoCases') || 0
    const chartData = _.orderBy(
        reasonKeys
            .map((r) => {
                const count = _.sumBy(data || [], `${r}_value`) || 0
                const pct = grandTotal ? Number(numberPrecision((count / grandTotal) * 100, 2)) : 0
                return { name: ndrReasons[r], count, percentage: pct }
            })
            .filter((row) => row.count > 0),
        ['count'],
        ['desc'],
    )

    return (
        <Card elevation={1}>
            <CardHeader
                title="RTO Reason Analysis"
                action={(
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ToggleButtonGroup color="primary" size="small" exclusive onChange={(e, v) => { if (v) setView(v) }} value={view}>
                            <ToggleButton value="chart" title="View Chart">
                                <BarChartIcon />
                            </ToggleButton>
                            <ToggleButton value="table" title="View Table">
                                <TableChartIcon />
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}
            />
            <CardContent>
                {view === 'chart' && ((!chartData || chartData.length === 0) ? <ChartEmpty height={480} /> : (
                    <ResponsiveContainer width="100%" height={480}>
                        <ComposedChart data={chartData} margin={{ top: 30, right: 30, bottom: 80, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#dee2e6" />
                            <XAxis dataKey="name" fontSize="11px" fontWeight="500" interval={0} angle={-30} textAnchor="end" height={100} label={{ value: 'RTO Reason', position: 'insideBottom', offset: -5, fontSize: 11, fill: '#666' }} />
                            <YAxis yAxisId="left" fontSize="11px" fontWeight="500" label={{ value: '# RTO', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} fontSize="11px" fontWeight="500" label={{ value: '% RTO', angle: -90, position: 'insideRight', fontSize: 11, fill: '#666', textAnchor: 'middle' }} />
                            <Tooltip content={<ChartTooltip />} />
                            <Legend verticalAlign="top" align="left" iconSize="10px" iconType="square" wrapperStyle={{ fontSize: 11, fontWeight: 500, paddingBottom: 10 }} />
                            <Bar yAxisId="left" dataKey="count" name="# RTO" fill={DUAL_AXIS_COLORS.PRIMARY} maxBarSize={40}>
                                <LabelList dataKey="count" position="top" style={{ fontSize: 10, fontWeight: 600, fill: '#374151' }} formatter={(v) => (v == null || v === 0) ? '' : v} />
                            </Bar>
                            <Bar yAxisId="right" dataKey="percentage" name="% RTO" fill={DUAL_AXIS_COLORS.SECONDARY} maxBarSize={40}>
                                <LabelList dataKey="percentage" position="top" style={{ fontSize: 10, fontWeight: 600, fill: DUAL_AXIS_COLORS.SECONDARY }} formatter={(v) => (v == null || v === 0) ? '' : `${v}%`} />
                            </Bar>
                        </ComposedChart>
                    </ResponsiveContainer>
                ))}
                {view === 'table' && (
                    <MaterialTable size="small" exportExcel={false} rows={rows} columns={tableColumns} frozenColumnsCount={1} />
                )}
            </CardContent>
        </Card>
    )
}
